import { Link } from 'react-router-dom'
import { Plane, Bus, Zap, Trees, ChevronRight, ArrowRight } from 'lucide-react'
import { SECONDARY_CTA_TEXT } from '../../../config/cta'
import LoopCTAs from '../../../components/LoopCTAs'
import { FadeIn } from '../../../components/Motion'
import Seo from '../../../components/Seo'

const subVerticals = [
  {
    icon: Plane,
    name: 'Airports',
    description: 'Passenger experience, terminal feedback, concessions.',
    href: '/industries/government/airports',
  },
  {
    icon: Bus,
    name: 'Public Transit',
    description: 'Rider experience, service reliability, accessibility.',
    href: '/industries/government/transit',
  },
  {
    icon: Zap,
    name: 'Utilities',
    description: 'Customer service, billing, outage communication.',
    href: '/industries/government/utilities',
  },
  {
    icon: Trees,
    name: 'Parks & Recreation',
    description: 'Visitor experience, program feedback, facilities.',
    href: '/industries/government/parks',
  },
]

const useCases = [
  'Citizen satisfaction surveys',
  'Service delivery feedback',
  'Public engagement research',
  'Website and digital experience',
  'Call center quality assessment',
  'Policy input and priorities',
  'Community program evaluation',
  'Accessibility improvements',
]

export default function GovernmentIndex() {
  return (
    <div>
      <Seo
        title="Government & Public Sector Citizen Feedback Surveys"
        description="PrismScope helps government agencies understand citizen needs across airports, transit, utilities, and parks—making every voice heard through adaptive conversational surveys."
        path="/industries/government"
      />
      {/* Breadcrumbs */}
      <div className="bg-white border-b-2 border-intelligence-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex items-center text-sm text-intelligence-500">
            <Link to="/" className="hover:text-intelligence-900">Home</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <Link to="/industries" className="hover:text-intelligence-900">Industries</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <span className="text-intelligence-900">Government</span>
          </nav>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <FadeIn>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-intelligence-500 mb-4">
              Industry
            </p>
            <h1 className="text-5xl md:text-6xl font-bold text-intelligence-900 mb-6 tracking-tight leading-[0.98]">
              Government &amp; Public Sector Feedback
            </h1>
            <p className="text-xl md:text-2xl text-intelligence-700 max-w-3xl mx-auto mb-10 leading-relaxed">
              Understand citizen needs across all public services—from airports to parks.
              Make every voice heard.
            </p>
            <div className="relative inline-block group">
              <div aria-hidden="true" className="absolute inset-0 rounded-lg bg-[#00008B] translate-x-1.5 translate-y-1.5 transition-transform duration-200 group-hover:translate-x-2 group-hover:translate-y-2" />
              <a href="https://rays.prismscope.ai/i/int_ONF-JNX1Zq0CyFEt0jzyGA?skip=1" className="relative inline-flex items-center px-8 py-4 text-lg font-bold text-white bg-intelligence-900 border-2 border-intelligence-900 rounded-lg">
                {SECONDARY_CTA_TEXT} <ArrowRight className="ml-2 w-5 h-5" />
              </a>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Sub-verticals */}
      <section className="bg-intelligence-100 py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-10">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-intelligence-500 mb-3">Choose your sector</p>
              <h2 className="text-3xl md:text-4xl font-bold text-intelligence-900">Choose Your Sector</h2>
            </div>
          </FadeIn>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {subVerticals.map((vertical, i) => {
              const colors = ['#3b82f6', '#8b5cf6', '#00008B', '#3b82f6']
              const color = colors[i % 3]
              return (
                <FadeIn key={vertical.name} delay={i * 0.04}>
                  <Link to={vertical.href} className="block group">
                    <div className="relative h-full">
                      <div aria-hidden="true" className="absolute inset-0 rounded-xl translate-x-1.5 translate-y-1.5 transition-transform duration-200 group-hover:translate-x-2 group-hover:translate-y-2" style={{ backgroundColor: color }} />
                      <div className="relative bg-white rounded-xl border-2 border-intelligence-900 p-6 h-full">
                        <vertical.icon className="w-7 h-7 text-intelligence-700 mb-3" />
                        <h3 className="font-bold text-intelligence-900 text-lg mb-1">{vertical.name}</h3>
                        <p className="text-intelligence-600 text-sm mb-3">{vertical.description}</p>
                        <span className="inline-flex items-center text-sm font-bold text-intelligence-900">
                          Explore <ArrowRight className="ml-1 w-4 h-4" />
                        </span>
                      </div>
                    </div>
                  </Link>
                </FadeIn>
              )
            })}
          </div>
        </div>
      </section>

      {/* Common Use Cases */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-10">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-intelligence-500 mb-3">What we cover</p>
              <h2 className="text-3xl md:text-4xl font-bold text-intelligence-900">Common Use Cases</h2>
            </div>
          </FadeIn>
          <div className="grid md:grid-cols-2 gap-4">
            {useCases.map((useCase, i) => (
              <FadeIn key={useCase} delay={i * 0.03}>
                <div className="bg-intelligence-100 rounded-lg border-2 border-intelligence-900 p-4 flex items-center shadow-[2px_2px_0_0_rgba(15,23,42,1)]">
                  <div className="w-2 h-2 bg-intelligence-900 rounded-full mr-3 flex-shrink-0" />
                  <span className="text-intelligence-700 font-medium">{useCase}</span>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-intelligence-900 py-20 md:py-24 relative overflow-hidden">
        <div aria-hidden="true" className="absolute inset-x-0 top-0 h-2 bg-gradient-to-r from-[#3b82f6] via-[#8b5cf6] to-[#00008B]" />
        <div aria-hidden="true" className="absolute inset-x-0 bottom-0 h-2 bg-gradient-to-r from-[#00008B] via-[#8b5cf6] to-[#3b82f6]" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <FadeIn>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-intelligence-400 mb-3">Your move</p>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">Hear from every citizen</h2>
            <p className="text-lg text-intelligence-400 mb-10">Run your first campaign. Try a 3-minute conversation first.</p>
            <LoopCTAs location="industry-government-index-final" align="center" />
          </FadeIn>
        </div>
      </section>
    </div>
  )
}
